import {
 CheckSquareFilled, DeleteOutlined, EditOutlined, PlusOutlined
} from '@ant-design/icons';
import {
  Button,
  Modal,
  Space,
  Tooltip,
  Typography,
  message
} from 'antd';
import { ColumnsType } from 'antd/lib/table';
import React, {
  useEffect, useMemo, useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAppSelector } from 'app/store';
import HeaderPage from 'common/components/HeaderPage';
import PageTable from 'common/components/PageTable';
import {
  FilterValueProps,
  mappingFilterToQuery, mappingParamsFilter, mappingQueryParamsFilter
} from 'common/components/PageTable/filter';
import { deleteExternalManagerService, getAllExternalManagerService } from 'common/services/externalManager';
import { ROUTE_PATHS } from 'common/utils/constant';
import { formatDateTime } from 'common/utils/functions';

export type ExternalManagerDataTypes = {
  id: number;
  name: string;
  apiKey: string;
  isActive: boolean;
  kioskDataName: string;
  creatorName: string;
  updaterName: string;
  createdAt: string;
  updatedAt: string;
};

const ExternalManagerManagement: React.FC<ActiveRoles> = ({
  roleCreate,
  roleDelete,
  roleUpdate
}) => {
  /* Hooks */
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  /* Selectors */
  const {
    defaultPageSize,
  } = useAppSelector((state) => state.system);
  const [searchParams, setSearchParams] = useSearchParams();

  const params = Object.fromEntries(searchParams.entries());

  /* States */
  const [currentPage, setCurrentPage] = useState(1);
  const [currentView, setCurrentView] = useState(defaultPageSize);
  const [keyword, setKeyword] = useState('');
  const [selectedFilterList, setSelectedFilterList] = useState<
    FilterValueProps[]>(mappingQueryParamsFilter(params));
  const returnParamsFilter = useMemo(
    () => mappingParamsFilter(selectedFilterList),
    [selectedFilterList]
  );

  const queryKey = ['getAllExternalManagerService', keyword, currentPage, currentView, returnParamsFilter];

  /* Queries */
  const { data: externalManagers, isLoading } = useQuery(
    queryKey,
    () => getAllExternalManagerService({
      keyword,
      page: currentPage,
      limit: currentView,
      ...returnParamsFilter
    }),
  );

  const { isLoading: deleteExternalLoading, mutate: deleteMutate } = useMutation(
    ['deleteExternalManagerMutate'],
    async (ids: number[]) => deleteExternalManagerService({ ids }),
    {
      onSuccess: () => {
        message.success(t('message.deleteSuccess'));
        queryClient.invalidateQueries(queryKey);
      },
      onError: () => {
        message.error(t('message.deleteError'));
      }
    }
  );

  const handleDelete = async (data: ExternalManagerDataTypes[]) => {
      deleteMutate(data.map((val) => val.id));
  };

  /* Variables */
  const columns: ColumnsType<ExternalManagerDataTypes> = [
    {
      title: 'ID',
      key: 'id',
      width: 55,
      align: 'center',
      fixed: 'left',
      render: (_name: string, data: ExternalManagerDataTypes) => (
        <Typography.Text>
          {data.id}
        </Typography.Text>
      ),
    },
    // --- Tiêu đề
    {
      title: t('system.name'),
      dataIndex: 'name',
      key: 'name',
      render: (_: string, data: ExternalManagerDataTypes) => (
        <Typography.Text>
          {data.name}
        </Typography.Text>
      ),
    },
    {
      title: t('externalManager.active'),
      dataIndex: 'isActive',
      key: 'isActive',
      render: (_: string, data: ExternalManagerDataTypes) => (
        <div className="">
          {data.isActive ? (
            <CheckSquareFilled style={{ fontSize: '16px', color: '#52c41a' }} />
          ) : ''}
        </div>
      ),
    },
    {
      title: 'API Key',
      dataIndex: 'apiKey',
      key: 'apiKey',
      render: (_: string, data: ExternalManagerDataTypes) => {
        const isLong = data.apiKey.length > 16;
        const displayKey = isLong ? `${data.apiKey.slice(0, 16)}...` : data.apiKey;

        return (
          <Space>
            <Tooltip title={isLong ? data.apiKey : undefined}>
              <Typography.Text>{displayKey}</Typography.Text>
            </Tooltip>
            <Typography.Text copyable={{ text: data.apiKey }} />
          </Space>
        );
      },
    },
    {
      title: t('tracking.kiosk'),
      dataIndex: 'kioskDataName',
      key: 'kioskDataName',
      render: (_: string, data: ExternalManagerDataTypes) => (
        <Typography.Text>
          {data.kioskDataName}
        </Typography.Text>
      ),
    },
    {
      title: t('system.creator'),
      dataIndex: 'creatorName',
      key: 'creatorName',
      render: (_: string, data: ExternalManagerDataTypes) => (
        <Typography.Text>
          {data.creatorName}
        </Typography.Text>
      ),
    },
    {
      title: t('system.editor'),
      dataIndex: 'updaterName',
      key: 'updaterName',
      render: (_: string, data: ExternalManagerDataTypes) => (
        <Typography.Text>
          {data.updaterName}
        </Typography.Text>
      ),
    },
    // --- Ngày tạo
    {
      title: t('system.createdAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (_: string, data: any) => (
        <Typography.Text
          style={{ color: '#4a4a4a' }}
        >
          {formatDateTime(data.createdAt)}
        </Typography.Text>
      ),
    },
    {
      title: t('system.updatedAt'),
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (_: string, data: any) => (
        <Typography.Text
          style={{ color: '#4a4a4a' }}
        >
          {formatDateTime(data.updatedAt)}
        </Typography.Text>
      ),
    },
    {
      title: t('system.action'),
      key: 'action',
      width: 100,
      align: 'center',
      render: (_: string, data: ExternalManagerDataTypes) => (
        <Space>
          <Button
            disabled={!roleUpdate}
            icon={<EditOutlined />}
            onClick={() => {
              navigate(`${ROUTE_PATHS.EXTERNAL_MANAGER_DETAIL}?id=${data.id}`);
            }}
          />
          <Button
            disabled={!roleDelete}
            icon={<DeleteOutlined />}
            onClick={() => {
              Modal.confirm({
                className: 't-pagetable_deleteRecordModal',
                autoFocusButton: 'cancel',
                title: t('message.confirmDeleteRecord'),
                okText: t('system.ok'),
                cancelText: t('system.cancel'),
                cancelButtonProps: {
                  type: 'primary',
                },
                okButtonProps: {
                  type: 'default',
                },
                onOk: () => {
                  handleDelete([data]);
                },
              });
            }}
          />
        </Space>
      ),
    },
  ];

  const externalData: ExternalManagerDataTypes[] = useMemo(() => (externalManagers?.data
    ?.map((val) => ({
    id: val.externalManagerData.id,
    name: val.externalManagerData.name,
    apiKey: val.externalManagerData.apiKey,
    kioskDataName: val.kioskData.name,
    creatorName: val.creator.name,
    updaterName: val.updater.name,
    isActive: val.externalManagerData.isActive,
    createdAt: val.externalManagerData.createdAt,
    updatedAt: val.externalManagerData.updatedAt,
  })) || []), [externalManagers?.data]);

  const handleSetCurrentPage = (page: number) => {
    setCurrentPage(page);
  };

  const handleSetCurrentView = (view: number) => {
    setCurrentView(view);
  };

  const handleFilter = (data: FilterValueProps) => {
    const counter = selectedFilterList.filter(
      (item) => item.field === data.field && item.filter === data.filter
    ).length;
    setSelectedFilterList([...selectedFilterList, {
      ...data,
      index: counter
    }]);
  };

  const handleDeleteFilter = (key: string, index?: number) => {
    const tempList = selectedFilterList.slice();
    setSelectedFilterList(tempList.filter((item) => !(item.key === key && item.index === index)));
  };

  useEffect(() => {
    setSearchParams(mappingFilterToQuery(selectedFilterList));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilterList]);

  /* Render */
  return (
    <>
      <HeaderPage
        fixed
        title={t('sidebar.externalManager')}
        rightHeader={(
          <Button
            type="primary"
            disabled={!roleCreate}
            onClick={() => roleCreate && navigate(`${ROUTE_PATHS.EXTERNAL_MANAGER_DETAIL}`)}
          >
            <PlusOutlined />
            {t('system.create')}
          </Button>
        )}
      />
      <div className="t-mainlayout_wrapper">
        <PageTable
          handleDelete={handleDelete}
          handleSearch={setKeyword}
          isLoading={isLoading || deleteExternalLoading}
          roles={{
            roleCreate,
            roleDelete,
            roleUpdate
          }}
          tableProps={{
            initShowColumns: ['id', 'name', 'isActive', 'apiKey', 'kioskDataName', 'createdAt', 'updatedAt', 'action'],
            columns,
            pageData: externalData,
            currentPage,
            pageSize: currentView,
            handleSetCurrentPage,
            handleSetCurrentView,
            total: externalManagers?.meta.total || 1,
            noBaseCol: true
          }}
          filtersDataTable={{
            handleFilter,
            selectedFilterList,
            handleDeleteFilter,
          }}
        />
      </div>
    </>
  );
};

export default ExternalManagerManagement;
